import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './productManagement.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons';
import FooterWeb from '../componets/footerwebsite/footerweb';
import Header from '../componets/headerwebsite/headerweb';
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import stockOutImage from '../../assets/stockout.png'; // Import stock out image
import { Button, Carousel, Form, Input, Modal, notification } from 'antd';
import { GET, POST } from '../../utils/apicalls';
import { API } from '../../config/api';
import { Heart, MessageCircle, Share2 } from 'lucide-react';


interface Product {
    id: number;
    productTitle: string;
    Price: number;
    descriptionProduct: string;
    brand: string;
    color: string;
    gender: string;
    size: string;
    shipping: string;
    stock_product?: string; // Add stock_product field
    productImage1?: string;
    productImage2?: string;
    productImage3?: string;
}

// Initialize dayjs plugins for timezone handling
dayjs.extend(utc);
dayjs.extend(timezone);
interface EngagementData {
    EngagementLike: number;
    EngagementComments: string[];
    EngagementShare: number;
}
function ProductManagement() {
    const [isLoading, setIsLoading] = useState(false);
    const [productData, setProductData] = useState<any>([]);
    const [offerdata, setofferdata] = useState<any>([]);
    const navigate = useNavigate();
    const [showScroll, setShowScroll] = useState(false);

    const [engagements, setEngagements] = useState<{ [productId: number]: EngagementData }>({});
    const [activeProduct, setActiveProduct] = useState<Product | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newComment, setNewComment] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        console.log('🔍 Current engagements state:', engagements);
    }, [engagements]);

    const fetchEngagementData = async (productId: number) => {
        try {
            const response: any = await GET(`${API.FIND_ONE_ENGAGEMENT_BUTTON}${productId}`, {});
            const data = response?.data || []; // ✅ Fix here


            if (!Array.isArray(data) || data.length === 0) {
                setEngagements((prev) => ({
                    ...prev,
                    [productId]: {
                        EngagementLike: 0,
                        EngagementShare: 0,
                        EngagementComments: [],
                    },
                }));
                return;
            }

            // ✅ Aggregate all engagement data
            let totalLikes = 0;
            let totalShares = 0;
            const allComments: string[] = [];

            data.forEach((item: any) => {
                const likes = Number(item.EngagementLike) || 0;
                const shares = Number(item.EngagementShare) || 0;
                totalLikes += likes;
                totalShares += shares;

                if (item.EngagementComments && item.EngagementComments.trim()) {
                    try {
                        const parsed = JSON.parse(item.EngagementComments);
                        if (Array.isArray(parsed)) {
                            allComments.push(...parsed.filter((c: string) => c && c.trim() && c !== "string"));
                        } else if (typeof parsed === "string" && parsed.trim() && parsed !== "string") {
                            allComments.push(parsed);
                        }
                    } catch {
                        if (item.EngagementComments !== "string" && item.EngagementComments.trim()) {
                            allComments.push(item.EngagementComments);
                        }
                    }
                }
            });

            const uniqueComments = Array.from(new Set(allComments));

            setEngagements((prev) => ({
                ...prev,
                [productId]: {
                    EngagementLike: totalLikes,
                    EngagementShare: totalShares,
                    EngagementComments: uniqueComments,
                },
            }));
        } catch (error) {
            console.error(`❌ Error fetching engagement for product ${productId}:`, error);
        }
    };


    useEffect(() => {
        if (productData?.length > 0) {
            productData.forEach((product: any) => {
                fetchEngagementData(product.id);
            });
        }
    }, [productData]);

    // ---------------------- Like Button ----------------------
    const handleLike = async (product: Product) => {
        try {
            const prev = engagements[product.id] || {
                EngagementLike: 0,
                EngagementComments: [],
                EngagementShare: 0,
            };


            // Update UI instantly
            setEngagements((prevData) => ({
                ...prevData,
                [product.id]: {
                    ...prev,
                    EngagementLike: prev.EngagementLike + 1
                }
            }));

            // Send only increment of 1
            const postResponse = await POST(API.CREATE_ENGAGEMENT_BUTTON, {
                productId: product.id,
                EngagementLike: 1,
                EngagementComments: JSON.stringify([]),
                EngagementShare: 0,
            });



            notification.success({ message: "Liked!" });

            // Refresh to get accurate count
            setTimeout(() => {
                fetchEngagementData(product.id);
            }, 500);
        } catch (error) {

            notification.error({ message: "Failed to like" });

            // Revert on error
            const prev = engagements[product.id];
            if (prev) {
                setEngagements((prevData) => ({
                    ...prevData,
                    [product.id]: {
                        ...prev,
                        EngagementLike: Math.max(0, prev.EngagementLike - 1)
                    }
                }));
            }
        }
    };

    // ---------------------- Comment Submit ----------------------
    const handleCommentSubmit = async (product: Product) => {
        if (!newComment.trim()) {
            notification.warning({ message: "Please enter a comment" });
            return;
        }

        const commentToAdd = newComment.trim();
        const prev = engagements[product.id] || {
            EngagementLike: 0,
            EngagementComments: [],
            EngagementShare: 0,
        };


        // Update UI instantly
        setEngagements((prevData) => ({
            ...prevData,
            [product.id]: {
                ...prev,
                EngagementComments: [...prev.EngagementComments, commentToAdd]
            }
        }));
        setNewComment("");
        setIsModalOpen(false);

        try {
            setLoading(true);

            // Send only the new comment
            const postResponse = await POST(API.CREATE_ENGAGEMENT_BUTTON, {
                productId: product.id,
                EngagementLike: 0,
                EngagementComments: JSON.stringify([commentToAdd]),
                EngagementShare: 0,
            });



            notification.success({ message: "Comment added!" });

            // Refresh
            setTimeout(() => {
                fetchEngagementData(product.id);
            }, 500);
        } catch (error) {
            notification.error({ message: "Failed to add comment" });

            // Revert UI
            setEngagements((prevData) => ({
                ...prevData,
                [product.id]: {
                    ...prev,
                    EngagementComments: prev.EngagementComments.filter(c => c !== commentToAdd)
                }
            }));
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        const checkScrollTop = () => {
            setShowScroll(window.scrollY > 300);
        };
        window.addEventListener('scroll', checkScrollTop);
        return () => window.removeEventListener('scroll', checkScrollTop);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const fetchProducts = async () => {
        try {
            setIsLoading(true);
            const response: any = await GET(API.PRODUCT_GET_LISTING, {});
            setProductData(response?.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    const shareOnWhatsApp = async (product: Product) => {
        const productOffers = Array.isArray(offerdata)
            ? offerdata.filter((offer) => offer.Product_id === product.id)
            : [];
        const offer = productOffers.length > 0 ? productOffers[0] : null;
        const finalPrice = offer ? offer.Offer_price : product.Price;
        const BASE_URL = 'https://mj4p.com';
        const productUrl = `${BASE_URL}/product/${product.id}`;
        const hasValue = (value: any) => {
            return value !== null &&
                value !== undefined &&
                value !== '' &&
                value !== '.' &&
                value.toString().trim() !== '' &&
                value.toString().trim() !== '.';
        };
        let messageParts = [];
        messageParts.push(
            `🛒 കേരളപ്ലസ് ഓൺലൈൻ ഷോപ്പിംഗ്\n📦 ഇന്ത്യ മുഴുവൻ ഡെലിവറി ലഭ്യമാണ്\n🤝 വിശ്വാസത്തോടെയും ഉറപ്പോടെയും...`
        );
        messageParts.push(`Buy here: ${productUrl}`);
        if (hasValue(product.productImage1)) {
            messageParts.push(`View product images at the link below`);
        }
        if (hasValue(product.productTitle)) {
            const stockStatus = product.stock_product === 'Out of Stock' ? ' (Out of Stock)' : '';
            messageParts.push(`*${product.productTitle}*${stockStatus}`);
        }
        if (hasValue(finalPrice)) {
            const priceText = offer ? `Rs.${finalPrice} (Offer Price)` : `Rs.${finalPrice}`;
            messageParts.push(`Price: ${priceText}`);
        }
        if (hasValue(product.descriptionProduct)) {
            messageParts.push(`Description: ${product.descriptionProduct}`);
        }
        if (hasValue(product.brand)) {
            messageParts.push(`Brand: ${product.brand}`);
        }
        if (hasValue(product.color)) {
            messageParts.push(`Color: ${product.color}`);
        }
        if (hasValue(product.gender)) {
            messageParts.push(`Gender: ${product.gender}`);
        }
        if (hasValue(product.size)) {
            messageParts.push(`Size: ${product.size}`);
        }
        if (hasValue(product.shipping)) {
            messageParts.push(`Shipping: ${product.shipping}`);
        }

        const message = `Check out this product:\n\n${messageParts.join('\n\n')}`;
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');

        // Track share
        try {
            const prev = engagements[product.id] || {
                EngagementLike: 0,
                EngagementComments: [],
                EngagementShare: 0,
            };


            // Update UI instantly
            setEngagements((prevData) => ({
                ...prevData,
                [product.id]: {
                    ...prev,
                    EngagementShare: prev.EngagementShare + 1
                }
            }));

            // Send increment
            await POST(API.CREATE_ENGAGEMENT_BUTTON, {
                productId: product.id,
                EngagementLike: 0,
                EngagementComments: JSON.stringify([]),
                EngagementShare: 1,
            });

            // Refresh
            setTimeout(() => {
                fetchEngagementData(product.id);
            }, 500);
        } catch (error) {
            console.error("❌ Error tracking share:", error);
        }
    };
    useEffect(() => {
        fetchofferproduct()
    }, []);
    const fetchofferproduct = async () => {
        try {
            setIsLoading(true);
            const url = API.ACTIVE_OFFER_PRODUCT;
            const response: any = await GET(url, {});

            if (response?.data && Array.isArray(response.data)) {
                setofferdata(response.data.map((item: any) => ({
                    ...item,
                    Offer_start_time: item.Offer_start_time ?
                        dayjs.utc(item.Offer_start_time).tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss') : '',
                    Offer_End_time: item.Offer_End_time ?
                        dayjs.utc(item.Offer_End_time).tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss') : ''
                })));
            } else {
                setofferdata([]); // Ensure it's always an array
            }
        } catch (error) {
            setofferdata([]); // Prevent undefined issues
        } finally {
            setIsLoading(false);
        }
    };

    const [countdowns, setCountdowns] = useState<{ [key: number]: string }>({});

    useEffect(() => {
        const updateCountdowns = () => {
            const newCountdowns: { [key: number]: string } = {};
            offerdata.forEach((offer: any) => {
                const offerEndTime = new Date(offer.Offer_End_time).getTime();
                const now = new Date().getTime();
                const timeLeft = offerEndTime - now;

                if (timeLeft <= 0) {
                    newCountdowns[offer.Product_id] = "Offer Expired!";
                } else {
                    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
                    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
                    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

                    newCountdowns[offer.Product_id] = ` ${hours}h ${minutes}m ${seconds}s`;
                }
            });

            setCountdowns(newCountdowns);
        };

        const timer = setInterval(updateCountdowns, 1000);
        updateCountdowns();

        return () => clearInterval(timer);
    }, [offerdata]);

    const [showFull, setShowFull] = useState(false);
    const [isInCart, setIsInCart] = useState(false);

    const handleAddToCart = async (product: any) => {
        try {
            let cartId = Number(localStorage.getItem('cartId'));
            if (!cartId) {
                const createCartResponse: any = await POST(API.CREATE_CART, {});
                cartId = createCartResponse?.id;
                localStorage.setItem('cartId', String(cartId));
            }

            const quantity = 1;
            const partnerId = Number(product.clientId || 1);

            // 🧩 Make sure offerdata exists
            if (!offerdata || !offerdata.length) {
                console.warn("⚠️ offerdata is empty");
            }

            // 🧠 Find matching offer
            const now = dayjs();
            const productOffer = offerdata?.find((offer: any) => {
                const offerProductId = Number(
                    offer.Product_id || offer.productId || offer.ProductId || offer.product?.id
                );

                // Check if this offer matches the current product
                if (offerProductId !== product.id) return false;

                // Check if offer time is valid
                const start = offer.Offer_start_time ? dayjs(offer.Offer_start_time) : null;
                const end = offer.Offer_End_time ? dayjs(offer.Offer_End_time) : null;
                const isActive =
                    (!start || now.isAfter(start)) &&
                    (!end || now.isBefore(end));

                return isActive;
            });

            // 💰 Decide final price
            const finalPrice = productOffer
                ? Number(productOffer.Offer_price)
                : Number(product.Price);



            const payload = {
                cartId: Number(cartId),
                productId: Number(product.id),
                partnerId: Number(partnerId),
                quantity,
                price: finalPrice,
                subtotal: quantity * finalPrice,
                totalPrice: quantity * finalPrice,
            };

            const addItemResponse: any = await POST(API.ADD_TO_CART, payload);

            if (addItemResponse?.status) {
                notification.success({ message: "✅ Product added to cart!" });
                setIsInCart(true);
            } else {
                notification.warning({
                    message:
                        addItemResponse?.message || "⚠️ Product already in cart",
                });
                setIsInCart(true);
            }
        } catch (err) {
            notification.error({ message: "❌ Failed to add product to cart" });
        }
    };

    // --- CART CHECK STATE ---
    const [cartHasItems, setCartHasItems] = useState(false);
    const [cartId, setCartId] = useState<string | null>(null);
    useEffect(() => {
        const checkCart = async () => {
            const storedCartId = localStorage.getItem('cartId');
            if (!storedCartId) return;

            try {
                const response: any = await GET(`${API.LISTING_CART_DATA}${storedCartId}`, {});
                if (response?.data && response.data.length > 0) {
                    setCartHasItems(true);
                    setCartId(storedCartId);
                } else {
                    setCartHasItems(false);
                }
            } catch (error) {
                setCartHasItems(false);
            }
        };

        checkCart();
    }, [isInCart]); // refresh whenever a new product is added
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <Header />

            {cartHasItems && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: '55px',
                        right: '10px',
                        zIndex: 9999,
                    }}
                >
                    <Button
                        type="primary"
                        size="large"
                        style={{
                            backgroundColor: '#073663ff',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px 24px',
                            boxShadow: '0px 4px 8px rgba(0,0,0,0.2)',
                        }}
                        onClick={() => navigate(`/cart/${cartId}`)}
                    >
                        Go to Cart
                    </Button>
                </div>
            )}
            <div className="product-container">
                {showScroll && (
                    <button className="scroll-top-button" onClick={scrollToTop}>
                        <FontAwesomeIcon icon={faArrowUp} />
                    </button>
                )}
                <div className="product-wrapper">
                    <h1 className="product-title">New Collections</h1>
                    <p className="text-center">See what new products are available in the online shop</p>

                    {isLoading ? (
                        <p className="loading-text">Loading...</p>
                    ) : (
                        <div className="product-list">
                            {(productData).map((product: any) => {
                                const productOffers = offerdata.filter((offer: any) =>
                                    offer.Product_id === product.id // Ensure correct property names
                                );
                                const offer = productOffers.length > 0 ? productOffers[0] : null;
                                const discountedPrice = offer ? offer.Offer_price : null;
                                const discountPercentage = offer && discountedPrice
                                    ? Math.round(((product.Price - discountedPrice) / product.Price) * 100)
                                    : null;

                                const isOutOfStock = product.stock_product === 'Out of Stock';


                                return (
                                    <div key={product.id} className="product-card">
                                        <div className="carousel-container" style={{ position: 'relative' }}>
                                            <Carousel autoplay autoplaySpeed={3000}>
                                                {[product.productImage1, product.productImage2, product.productImage3, product.productImage4]
                                                    .filter(Boolean)
                                                    .map((image, imgIndex) => (
                                                        <img
                                                            key={imgIndex}
                                                            src={image}
                                                            alt={product.productTitle}
                                                            className="carousel-images"
                                                            onClick={() => navigate(`/product/${product.id}`)}
                                                            style={{ opacity: isOutOfStock ? '1' : '1' }}
                                                        />
                                                    ))}
                                            </Carousel>

                                            {/* Display "Out of Stock" overlay if product is out of stock */}
                                            {isOutOfStock && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        top: '10%',
                                                        right: '5%', // Adjust how far from the right edge you want it
                                                        transform: 'translateY(-50%)', // centers vertically
                                                        zIndex: 10
                                                    }}

                                                >
                                                    <img
                                                        src={stockOutImage}
                                                        alt="Out of Stock"
                                                        style={{
                                                            width: '80px',
                                                            maxWidth: '80%'
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>


                                        <div className="offer-section">
                                            {offer ? (
                                                <>
                                                    <p className="offer-text">
                                                        <span className="offer-percentage">{discountPercentage}% OFF</span>
                                                        <span className="original-price">Rs. {product.Price}</span>
                                                        <span className="discounted-price">Rs. {discountedPrice}</span>
                                                    </p>
                                                    {/* <p className="offer-time">
                                                        Offer valid from:
                                                        <span className="offer-time-value">{offer.Offer_start_time}</span>
                                                        to
                                                        <span className="offer-time-value">{offer.Offer_End_time}</span>
                                                    </p> */}
                                                    <p className="countdown-timer">
                                                        Offer Time Left: <b>{countdowns[product.id] || "No Offer"}</b>
                                                    </p>
                                                </>
                                            ) : (
                                                <p className="price">Rs. <b>{product.Price}</b></p>
                                            )}
                                        </div>

                                        <div className="function-button-product">
                                            <Button
                                                onClick={() => handleAddToCart(product)} // ✅ FIXED HERE
                                                disabled={isOutOfStock}
                                            >
                                                {isOutOfStock ? 'OUT OF STOCK' : 'ADD TO CART'}
                                            </Button>


                                            <div className="button-rows">
                                                {/* Share */}
                                                <div className="icon-buttons icon-button share" onClick={() => shareOnWhatsApp(product)}>
                                                    <Share2 size={18} />
                                                    <span className="like-count">
                                                        {engagements[product.id]?.EngagementShare || 0}
                                                    </span>
                                                </div>

                                                {/* Comment */}
                                                <div
                                                    className="icon-buttons icon-button comment"
                                                    onClick={() => {
                                                        setActiveProduct(product);
                                                        setIsModalOpen(true);
                                                    }}
                                                >
                                                    <MessageCircle size={18} />
                                                    <span className="like-count">
                                                        {engagements[product.id]?.EngagementComments?.length || 0}
                                                    </span>
                                                </div>

                                                {/* Like */}
                                                <div className="like-section">
                                                    <div
                                                        className={`icon-buttons icon-button like ${engagements[product.id]?.EngagementLike > 0 ? "active" : ""
                                                            }`}
                                                        onClick={() => handleLike(product)}
                                                    >
                                                        <Heart
                                                            size={18}
                                                            fill={engagements[product?.id]?.EngagementLike > 0 ? "red" : "none"}
                                                        />
                                                    </div>
                                                    <span className="like-count">
                                                        {engagements[product.id]?.EngagementLike || 0}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 💬 Comment Modal */}
                                        <Modal
                                            title="Comments"
                                            open={isModalOpen}
                                            onCancel={() => setIsModalOpen(false)}
                                            footer={null}
                                        >
                                            <div style={{ maxHeight: "150px", overflowY: "auto", marginBottom: "10px" }}>
                                                {activeProduct && engagements[activeProduct.id]?.EngagementComments?.length ? (
                                                    engagements[activeProduct.id].EngagementComments.map((cmt, index) => (
                                                        <p key={index} style={{ borderBottom: "1px solid #eee", padding: "5px 0" }}>
                                                            {cmt}
                                                        </p>
                                                    ))
                                                ) : (
                                                    <p style={{ color: "#aaa" }}>No comments yet</p>
                                                )}
                                            </div>

                                            <Input.TextArea
                                                rows={2}
                                                value={newComment}
                                                onChange={(e) => setNewComment(e.target.value)}
                                                placeholder="Write a comment..."
                                            />

                                            <Button
                                                type="primary"
                                                block
                                                style={{ marginTop: "10px" }}
                                                onClick={() => activeProduct && handleCommentSubmit(activeProduct)}
                                                loading={loading}
                                            >
                                                Add Comment
                                            </Button>
                                        </Modal>

                                        <div>
                                            {/* <Button onClick={() => shareOnWhatsApp(product)}>SHARE</Button> */}
                                        </div>

                                        <h2 className="product-name">{product.productTitle}</h2>
                                        <div>
                                            <p style={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: showFull ? 'none' : 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden'
                                            }}>
                                                {product.descriptionProduct}
                                            </p>
                                            {product.descriptionProduct.length > 50 && (
                                                <span
                                                    onClick={() => setShowFull(!showFull)}
                                                    style={{ color: 'blue', cursor: 'pointer' }}
                                                >
                                                    {showFull ? 'Read Less' : 'Read More'}
                                                </span>
                                            )}
                                        </div>
                                        <Button onClick={() => navigate(`/product/${product.id}`)}>View Details</Button>
                                    </div>
                                );
                            })}

                        </div>
                    )}
                </div>
            </div>
            <FooterWeb />
        </>
    );
}

export default ProductManagement;